import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './News.css';
import { getNews, getNewsCategories, getFileUrl } from '../services/apiService';
import { getExcerpt } from '../utils/htmlUtils';
import primaLogo from '../aseets/images/prima_logo.png';
import slide1img from '../aseets/images/home/slide1.avif';
import slide2img from '../aseets/images/home/slide2.avif';
import slide3img from '../aseets/images/home/slide3.jpg';
import img1 from '../aseets/images/home/img1.jpg';
import img2 from '../aseets/images/home/img2.jpg';
import img3 from '../aseets/images/home/img3.jpg';
import img4 from '../aseets/images/home/img4.jpg';
import img5 from '../aseets/images/home/img5.jpg';
import img6 from '../aseets/images/home/img6.jpg';

const News = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [selectedArchive, setSelectedArchive] = useState(null); // 'YYYY-MM' or null
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fonction pour mapper les catégories de la base de données vers les catégories du frontend
  const mapCategoryToFrontend = (dbCategory) => {
    const categoryMap = {
      'Agriculture': 'agriculture',
      'Innovation': 'innovation',
      'Durabilité': 'sustainability',
      'Technologie': 'technology',
      'Projet': 'innovation',
      'Circulaire': 'sustainability',
      'Formation': 'agriculture',
      'Pratiques': 'agriculture',
      'Partenariat': 'innovation',
      'Actualité': 'innovation'
    };
    return categoryMap[dbCategory] || dbCategory.toLowerCase();
  };

  // Fonction pour transformer les données de l'API vers le format attendu par le frontend
  const transformNewsData = (newsData) => {
    return newsData.map(news => {
      // Garder les catégories originales de la base de données (noms complets)
      // et aussi créer une version mappée pour le filtrage
      const originalCategories = news.categories || [];
      // Pour le filtrage, utiliser les noms originaux normalisés en minuscules
      // Cela permet de filtrer directement par le nom de la catégorie dans la base de données
      const frontendCategories = originalCategories.map(cat => {
        // Utiliser le nom original normalisé en minuscules pour le filtrage
        return (cat || '').toLowerCase().trim();
      });
      
      // Obtenir l'image (image_url ou image_file_path ou premier média image)
      let imageUrl = news.image_url;
      if (!imageUrl && news.image_file_path) {
        imageUrl = getFileUrl(news.image_file_path);
      }
      if (!imageUrl && news.media && news.media.length > 0) {
        const firstImage = news.media.find(m => m.media_type === 'image');
        if (firstImage) {
          imageUrl = firstImage.url ? getFileUrl(firstImage.url) : getFileUrl(firstImage.file_path);
        }
      }
      
      // Utiliser content comme excerpt (extract plain text from HTML)
      const excerpt = news.content ? getExcerpt(news.content, 150) : '';
      
      return {
        id: news.id,
        title: news.title,
        date: news.created_at || news.date,
        categories: frontendCategories, // legacy name-based filter values
        category_ids: Array.isArray(news.category_ids) ? news.category_ids : [],
        originalCategories: originalCategories, // Noms originaux de la base de données pour l'affichage (format: 'Agriculture', 'Innovation', etc.)
        href: `/news/${news.id}`,
        img: imageUrl || 'https://via.placeholder.com/400x300?text=News',
        excerpt: excerpt,
        author: news.author_name || news.author || 'Admin',
        readTime: Math.ceil((news.content || '').length / 1000) || 5, // Estimation basée sur la longueur
        views: news.views || 0,
        featured: false
      };
    });
  };

  // Récupérer les news depuis l'API
  useEffect(() => {
    const fetchNews = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Récupérer seulement les news publiées
        const newsData = await getNews({ status: 'Publié' });
        const transformedPosts = transformNewsData(newsData);
        setPosts(transformedPosts);
        
        // Récupérer les catégories depuis la base de données
        const categoriesData = await getNewsCategories({ status: 'Publié' });
        const safeCategories = (categoriesData || [])
          .map((c) => ({
            id: c?.id,
            name: c?.name
          }))
          .filter((c) => c.id && typeof c.name === 'string' && c.name.trim() !== '');

        const categoriesList = [
          { id: 'all', name: t('news.categories.all', 'All categories') },
          ...safeCategories
        ];
        setCategories(categoriesList);
      } catch (err) {
        setError(t('news.error'));
        setPosts([]); // Utiliser un tableau vide en cas d'erreur
        setCategories([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNews();
  }, [t]);

  // Handle URL parameters for category and archive filtering
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    // Récupérer toutes les catégories de l'URL (support de plusieurs catégories)
    const categoryParams = urlParams.getAll('category');
    const archiveParam = urlParams.get('archive');
    
    if (categoryParams.length > 0) {
      // Normaliser toutes les catégories en minuscules
      const normalizedCategories = categoryParams
        .filter(cat => cat && cat !== 'all')
        .map(cat => cat.toLowerCase().trim());
      setSelectedCategories(normalizedCategories);
    } else {
      // Si aucun paramètre category dans l'URL, réinitialiser les catégories sélectionnées
      setSelectedCategories([]);
    }
    
    if (archiveParam && archiveParam !== 'all') {
      setSelectedArchive(archiveParam);
    } else if (!archiveParam) {
      setSelectedArchive(null);
    }
  }, [location.search]);

  // Calculer les archives dynamiquement depuis les posts
  const archives = useMemo(() => {
    const archiveMap = new Map();
    
    posts.forEach(post => {
      if (post.date) {
        const date = new Date(post.date);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const key = `${year}-${String(month).padStart(2, '0')}`;
        
        if (!archiveMap.has(key)) {
          const monthNames = [
            t('news.months.january'),
            t('news.months.february'),
            t('news.months.march'),
            t('news.months.april'),
            t('news.months.may'),
            t('news.months.june'),
            t('news.months.july'),
            t('news.months.august'),
            t('news.months.september'),
            t('news.months.october'),
            t('news.months.november'),
            t('news.months.december')
          ];
          const label = `${year} · ${monthNames[month - 1]}`;
          archiveMap.set(key, { key, label, count: 0 });
        }
        archiveMap.get(key).count += 1;
      }
    });

    // Convertir en tableau et trier par date (plus récent en premier)
    const archiveArray = Array.from(archiveMap.values())
      .sort((a, b) => b.key.localeCompare(a.key));

    return [
      { key: 'all', label: t('news.archives.all', 'Toutes les dates'), count: posts.length },
      ...archiveArray
    ];
  }, [posts, t]);

  // Les catégories sont maintenant récupérées directement depuis la base de données
  // Plus besoin de les calculer côté frontend
  // Memoized filtered and sorted posts for better performance
  const filteredPosts = useMemo(() => {
    let filtered = posts.filter(post => {
      const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           post.author.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtrage par catégories : comparer les IDs des catégories sélectionnées (noms originaux en minuscules)
      // avec les catégories du post (noms originaux normalisés en minuscules)
      // Category filtering by category IDs returned by API
      const matchesCategory =
        selectedCategories.length === 0 ||
        (post.category_ids && post.category_ids.length > 0 &&
          selectedCategories.some((selectedId) => post.category_ids.includes(selectedId)));
      
      const postKey = (post.date || '').slice(0, 7);
      const matchesArchive = !selectedArchive || postKey === selectedArchive;
      return matchesSearch && matchesCategory && matchesArchive;
    });

    // Sort posts based on selected criteria
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
        break;
      default:
        break;
    }

    return filtered;
  }, [posts, searchTerm, selectedCategories, selectedArchive, sortBy]);

  // Memoized category counts for better performance
  const categoryCounts = useMemo(() => {
    const counts = {};
    posts.forEach(post => {
      post.categories.forEach(category => {
        counts[category] = (counts[category] || 0) + 1;
      });
    });
    return counts;
  }, [posts]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '';
    // Si c'est déjà au format YYYY-MM-DD, le retourner tel quel
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }
    // Sinon, extraire seulement la partie date
    try {
    const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      // Si c'est une chaîne au format ISO, extraire seulement la partie date
      if (typeof dateString === 'string' && dateString.includes('T')) {
        return dateString.split('T')[0];
      }
      return '';
    }
  }, []);

  const toggleCategory = useCallback((categoryId) => {
    setSearchTerm('');
    if (categoryId === 'all') {
      setSelectedCategories([]);
      setCurrentPage(1);
      // Mettre à jour l'URL pour supprimer le paramètre category
      navigate('/news', { replace: true });
      return;
    }
    setSelectedCategories(prev => {
      // Normaliser l'ID de la catégorie en minuscules
      const normalizedId = categoryId.toLowerCase().trim();
      const exists = prev.some(c => c.toLowerCase().trim() === normalizedId);
      if (exists) {
        // Désélectionner la catégorie
        const next = prev.filter(c => c.toLowerCase().trim() !== normalizedId);
        setCurrentPage(1);
        // Mettre à jour l'URL avec toutes les catégories restantes
        if (next.length === 0) {
          navigate('/news', { replace: true });
        } else {
          // Construire l'URL avec toutes les catégories sélectionnées
          const categoryParams = next.map(cat => `category=${encodeURIComponent(cat)}`).join('&');
          navigate(`/news?${categoryParams}`, { replace: true });
        }
        return next;
      }
      // Sélectionner la catégorie
      const next = [...prev, normalizedId];
      setCurrentPage(1);
      // Mettre à jour l'URL avec toutes les catégories sélectionnées
      const categoryParams = next.map(cat => `category=${encodeURIComponent(cat)}`).join('&');
      navigate(`/news?${categoryParams}`, { replace: true });
      return next;
    });
  }, [navigate]);

  const toggleArchive = useCallback((archiveKey) => {
    setCurrentPage(1);
    if (archiveKey === 'all') {
      setSelectedArchive(null);
      return;
    }
    setSelectedArchive(prev => (prev === archiveKey ? null : archiveKey));
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const renderSearchForm = () => (
    <div className="search-box">
      <form
        className="search-form"
        onSubmit={(e) => {
          e.preventDefault();
          setCurrentPage(1);
        }}
      >
        <div className="search-field">
          <input
            type="search"
            className="search-input"
            placeholder={t('news.search.placeholder', 'Rechercher des articles...')}
            value={searchTerm}
            onChange={handleSearchChange}
            aria-label={t('news.search.placeholder', 'Rechercher des articles...')}
          />
          {searchTerm && (
            <button
              type="button"
              className="search-clear"
              onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
              }}
              aria-label={t('news.search.clear', 'Effacer la recherche')}
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          )}
          <button
            type="submit"
            className="search-submit"
            aria-label={t('news.search.submit', 'Rechercher')}
          >
            <i className="fas fa-search" aria-hidden="true" />
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <>
      {/* Popup Filtres Mobile */}
      {isMobileFilterOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: '80px',
          right: 0,
          bottom: 0,
          background: '#ffffff',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            padding: '10px 20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: '#96ca2d',
            color: '#ffffff',
            flexShrink: 0
          }}>
            <h3 style={{ paddingTop:"10px", fontSize: '20px', fontWeight: 'bold', color: '#ffffff' }}>
              {t('news.filters.title', 'Filtres')}
            </h3>
            <button
              onClick={() => setIsMobileFilterOpen(false)}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: '#ffffff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px'
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

            {/* Contenu des filtres */}
            <div style={{ 
              padding: '20px', 
              flex: 1, 
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Articles récents */}
              <div style={{ marginBottom:0 }}>
                <h4 className="results-title" style={{ fontSize: "1.1rem", marginBottom: 20, fontWeight: 'bold' }}>{t('news.recent', 'Articles récents')}</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
                  {posts.slice(0,5).map(p => (
                    <li key={p.id} style={{ display: 'grid', gridTemplateColumns: '64px 1fr', gap: 8, alignItems: 'center' }}>
                      <img src={p.img} alt={p.title} style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                      <div>
                        <a 
                          href="#" 
                          onClick={(e) => { e.preventDefault(); scrollToTop(); navigate(`/news/${p.id}`); }}
                          style={{ 
                            fontSize: '1.1rem',
                            color: '#212529', 
                            textDecoration: 'none',
                            transition: 'color 0.2s ease, text-decoration 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = '#96ca2d';
                            e.target.style.textDecoration = 'underline';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = '#212529';
                            e.target.style.textDecoration = 'none';
                          }}
                          onFocus={(e) => {
                            e.target.style.color = '#96ca2d';
                            e.target.style.textDecoration = 'underline';
                          }}
                          onBlur={(e) => {
                            e.target.style.color = '#212529';
                            e.target.style.textDecoration = 'none';
                          }}
                        >
                          {p.title}
                        </a>
                        <div style={{ fontSize: 12, color: '#6c757d', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <i className="fas fa-calendar-alt" style={{ fontSize: '0.7rem', color: '#6c757d' }}></i>
                          <span>{formatDate(p.date)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Archives */}
              <div style={{ marginBottom: 0 }}>
                <h4 className="results-title" style={{ fontSize: 18, marginBottom: 8, fontWeight: 'bold' }}>{t('news.archives.title', 'Archives')}</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 4 }}>
                  {archives.map(a => (
                    <li key={a.key}>
                      <span 
                        style={{ 
                          display: 'block', 
                          color: (selectedArchive===a.key || (a.key==='all' && !selectedArchive)) ? '#96ca2d' : '#6c757d', 
                          cursor: 'pointer', 
                          paddingLeft: '0px', 
                          fontSize: '15px',
                          textDecoration: 'none',
                          transition: 'color 0.2s ease, text-decoration 0.2s ease'
                        }} 
                        onClick={() => toggleArchive(a.key)}
                        onMouseEnter={(e) => {
                          e.target.style.color = '#96ca2d';
                          e.target.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = (selectedArchive===a.key || (a.key==='all' && !selectedArchive)) ? '#96ca2d' : '#6c757d';
                          e.target.style.textDecoration = 'none';
                        }}
                        onFocus={(e) => {
                          e.target.style.color = '#96ca2d';
                          e.target.style.textDecoration = 'underline';
                        }}
                        onBlur={(e) => {
                          e.target.style.color = (selectedArchive===a.key || (a.key==='all' && !selectedArchive)) ? '#96ca2d' : '#6c757d';
                          e.target.style.textDecoration = 'none';
                        }}
                      >
                        {a.label}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Catégories */}
              <div className="widget" style={{ marginBottom: 12 }}>
                <h4 className="results-title" style={{ fontSize: 18, marginBottom: 8, fontWeight: 'bold' }}>{t('news.categories.title', 'Catégories')}</h4>
                <div className="category-filters" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', marginBottom: 8, gap: 4 }}>
                  {categories.map(cat => {
                    const isChecked = cat.id === 'all' ? selectedCategories.length === 0 : selectedCategories.includes(cat.id);
                    return (
                      <label
                        key={cat.id}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleCategory(cat.id);
                        }}
                    style={{ 
                      width: '100%', 
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                      cursor: 'pointer', 
                      paddingLeft: '12px', 
                      fontSize: '15px',
                          color: isChecked ? '#96ca2d' : '#6c757d',
                          transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#96ca2d';
                    }}
                    onMouseLeave={(e) => {
                          e.currentTarget.style.color = isChecked ? '#96ca2d' : '#6c757d';
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleCategory(cat.id)}
                          onClick={(e) => e.stopPropagation()}
                      style={{ 
                            width: '18px',
                            height: '18px',
                        cursor: 'pointer', 
                            accentColor: '#96ca2d',
                            flexShrink: 0
                          }}
                        />
                        <span>{cat.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

               {/* Boutons Footer */}
               <div style={{
                 marginTop: 'auto',
                 display: 'flex',
                 flexDirection: 'column',
                 gap: '12px',
                 paddingTop: '20px',
                 flexShrink: 0
               }}>
                 <button
                   onClick={() => {
                     setSelectedCategories([]);
                     setSelectedArchive(null);
                     setSearchTerm('');
                     setSortBy('newest');
                     setCurrentPage(1);
                   }}
                   style={{
                     flex: 1,
                     padding: '14px 20px',
                     borderRadius: '12px',
                     background: '#6c757d',
                     color: '#ffffff',
                     border: 'none',
                     fontSize: '16px',
                     fontWeight: '600',
                     cursor: 'pointer',
                     transition: 'all 0.3s ease',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     gap: '8px'
                   }}
                   onMouseEnter={(e) => {
                     e.target.style.background = '#5a6268';
                     e.target.style.transform = 'translateY(-2px)';
                   }}
                   onMouseLeave={(e) => {
                     e.target.style.background = '#6c757d';
                     e.target.style.transform = 'translateY(0)';
                   }}
                 >
                   <i className="fas fa-undo" style={{ fontSize: '14px' }}></i>
                   {t('news.filters.reset', 'Réinitialiser')}
                 </button>
                 
                 <button
                   onClick={() => setIsMobileFilterOpen(false)}
                   style={{
                     flex: 1,
                     padding: '14px 20px',
                     borderRadius: '12px',
                     background: 'linear-gradient(135deg, #96ca2d 0%, #7ba818 100%)',
                     color: '#ffffff',
                     border: 'none',
                     fontSize: '16px',
                     fontWeight: '600',
                     cursor: 'pointer',
                     transition: 'all 0.3s ease',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     gap: '8px',
                     boxShadow: '0 4px 12px rgba(150, 202, 45, 0.3)'
                   }}
                   onMouseEnter={(e) => {
                     e.target.style.transform = 'translateY(-2px)';
                     e.target.style.boxShadow = '0 6px 16px rgba(150, 202, 45, 0.4)';
                   }}
                   onMouseLeave={(e) => {
                     e.target.style.transform = 'translateY(0)';
                     e.target.style.boxShadow = '0 4px 12px rgba(150, 202, 45, 0.3)';
                   }}
                 >
                   <i className="fas fa-check" style={{ fontSize: '14px' }}></i>
                   {t('news.filters.apply', 'Appliquer')}
                 </button>
               </div>
            </div>
        </div>
      )}

      <div className="home page-template page-template-page-templates page-template-fullwidth-content page-template-page-templatesfullwidth-content-php page page-id-4059 jet-theme-core jet-theme-core--has-template jet-theme-core--has-header jet-theme-core--has-body jet-theme-core--has-footer layout-fullwidth blog-masonry elementor-default elementor-kit-5747 elementor-page elementor-page-4059"><div id="jet-theme-core-document" className="jet-theme-core-document jet-theme-core-document--elementor-content-type"><div className="jet-theme-core-document__inner">
	
	{!isMobileFilterOpen && <Navbar/>}
	
	{/* Composant de test des icônes FontAwesome */}
	
	<article id="post-4059" class="post-4059 page type-page status-publish hentry">	
			<div data-elementor-type="wp-page" data-elementor-id="4059" class="elementor elementor-4059">
				<section class="elementor-section elementor-top-section elementor-element elementor-element-ic2p76x elementor-section-full_width elementor-section-height-default elementor-section-height-default" data-id="ic2p76x" data-element_type="section" data-settings="{&quot;jet_parallax_layout_list&quot;:[{&quot;jet_parallax_layout_image&quot;:{&quot;url&quot;:&quot;&quot;,&quot;id&quot;:&quot;&quot;,&quot;size&quot;:&quot;&quot;},&quot;_id&quot;:&quot;5759a04&quot;,&quot;jet_parallax_layout_image_tablet&quot;:{&quot;url&quot;:&quot;&quot;,&quot;id&quot;:&quot;&quot;,&quot;size&quot;:&quot;&quot;},&quot;jet_parallax_layout_image_mobile&quot;:{&quot;url&quot;:&quot;&quot;,&quot;id&quot;:&quot;&quot;,&quot;size&quot;:&quot;&quot;},&quot;jet_parallax_layout_speed&quot;:{&quot;unit&quot;:&quot;%&quot;,&quot;size&quot;:50,&quot;sizes&quot;:[]},&quot;jet_parallax_layout_type&quot;:&quot;scroll&quot;,&quot;jet_parallax_layout_direction&quot;:null,&quot;jet_parallax_layout_fx_direction&quot;:null,&quot;jet_parallax_layout_z_index&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_x&quot;:50,&quot;jet_parallax_layout_bg_x_tablet&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_x_mobile&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_y&quot;:50,&quot;jet_parallax_layout_bg_y_tablet&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_y_mobile&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_size&quot;:&quot;auto&quot;,&quot;jet_parallax_layout_bg_size_tablet&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_size_mobile&quot;:&quot;&quot;,&quot;jet_parallax_layout_animation_prop&quot;:&quot;transform&quot;,&quot;jet_parallax_layout_on&quot;:[&quot;desktop&quot;,&quot;tablet&quot;]}]}">
					<div class="elementor-container elementor-column-gap-default">
						<div class="elementor-column elementor-col-100 elementor-top-column elementor-element elementor-element-im9c73l" data-id="im9c73l" data-element_type="column">
							<div class="elementor-widget-wrap elementor-element-populated">
								<div class="elementor-element elementor-element-0bd4fc3 elementor-widget elementor-widget-jet-slider" data-id="0bd4fc3" data-element_type="widget" data-settings="{&quot;slider_height&quot;:{&quot;unit&quot;:&quot;px&quot;,&quot;size&quot;:852,&quot;sizes&quot;:[]},&quot;_animation&quot;:&quot;none&quot;,&quot;slider_height_tablet&quot;:{&quot;unit&quot;:&quot;px&quot;,&quot;size&quot;:650,&quot;sizes&quot;:[]},&quot;slider_height_mobile&quot;:{&quot;unit&quot;:&quot;px&quot;,&quot;size&quot;:500,&quot;sizes&quot;:[]}}" data-widget_type="jet-slider.default">
									<div class="elementor-widget-container">
										<div class="elementor-jet-slider jet-elements">
											<div style={{ position: 'relative', width: '100%', height: '500px', overflow: 'hidden' }}>
												<img src={slide3img} alt="News hero" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
												<div style={{ position: 'absolute', inset: 0, background: 'rgba(0, 0, 0, 0.45)' }} />
												<div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
													<div style={{ background: 'rgba(0,0,0,0.45)', padding: '16px 24px', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.25)', backdropFilter: 'blur(2px)', maxWidth: '960px', width: '100%' }}>
														<h1 style={{ color: '#fff', fontSize: 'clamp(28px,5vw,46px)', fontWeight: 800, margin: 0, textAlign: 'center', letterSpacing: '0.5px' }}>{t('news.heroTitle', 'Découvrez nos dernières actualités, projets et réussites')}</h1>
														<p style={{ color: 'rgba(255,255,255,0.95)', fontSize: 'clamp(14px,2.5vw,18px)', lineHeight: 1.6, margin: '12px auto 0', textAlign: 'center', maxWidth: '800px' }}>{t('news.heroSubtitle', 'Restez informé des innovations, événements et avancées du consortium MedAgriCycle à travers la Méditerranée.')}</p>
</div>
</div>
</div>
</div>
</div>
</div>
</div>
</div>
</div>
</section>
</div>
                    </article>

		{/* News Layout */}
        <section className="news-content">
			<div className="container news-layout-container">
				<main>
					<div className="mobile-toolbar">
						{renderSearchForm()}

						<div className="mobile-toolbar__filters">
							<button
								type="button"
								className="mobile-filter-btn"
								onClick={() => setIsMobileFilterOpen(true)}
								title={t('news.filters.open', 'Ouvrir les filtres')}
							>
								<i className="fas fa-filter" aria-hidden="true" />
								<span>{t('news.filters.open', 'Filtres')}</span>
							</button>
						</div>

						<div className="mobile-toolbar__sort">
							<h4 className="results-title" style={{ fontSize: '1.1rem', fontWeight: 'bold', margin: 0 }}>
								{t('news.sort.title', 'Trier par')}
							</h4>
							<button
								type="button"
								className="mobile-toolbar__sort-btn"
								onClick={() => setSortBy(sortBy === 'newest' ? 'oldest' : 'newest')}
								aria-label={sortBy === 'newest' ? t('news.sort.changeToOldest') : t('news.sort.changeToNewest')}
							>
								<svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" aria-hidden="true" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
									<path d="M304 416h-64a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h64a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zM16 160h48v304a16 16 0 0 0 16 16h32a16 16 0 0 0 16-16V160h48c14.21 0 21.38-17.24 11.31-27.31l-80-96a16 16 0 0 0-22.62 0l-80 96C-5.35 142.74 1.77 160 16 160zm416 0H240a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h192a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zm-64 128H240a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h128a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zM496 32H240a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h256a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16z" />
								</svg>
								<span>{sortBy === 'newest' ? t('news.sort.newestToOldest') : t('news.sort.oldestToNewest')}</span>
							</button>
						</div>
					</div>

                <div className="news-grid" style={{ 
					display: 'flex', 
					flexWrap: 'wrap', 
					gap: '30px', 
					justifyContent: 'center' 
				}}>
					{isLoading ? (
						<div style={{ 
							width: '100%', 
							textAlign: 'center', 
							padding: '60px 20px',
							color: '#6c757d'
						}}>
							<i className="fas fa-spinner fa-spin" style={{ fontSize: '48px', marginBottom: '20px', color: '#96ca2d' }}></i>
							<p style={{ fontSize: '18px', fontWeight: '500' }}>{t('news.loading')}</p>
						</div>
					) : error ? (
						<div style={{ 
							width: '100%', 
							textAlign: 'center', 
							padding: '60px 20px',
							color: '#dc2626'
						}}>
							<i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', marginBottom: '20px' }}></i>
							<p style={{ fontSize: '18px', fontWeight: '500' }}>{error}</p>
							<button
								onClick={() => window.location.reload()}
								style={{
									marginTop: '20px',
									padding: '12px 24px',
									background: '#96ca2d',
									color: 'white',
									border: 'none',
									borderRadius: '8px',
									cursor: 'pointer',
									fontSize: '16px',
									fontWeight: '600'
								}}
							>
								{t('news.retry')}
							</button>
						</div>
					) : filteredPosts.length === 0 ? (
						<div style={{ 
							width: '100%', 
							textAlign: 'center', 
							padding: '60px 20px',
							color: '#6c757d'
						}}>
							<i className="fas fa-newspaper" style={{ fontSize: '48px', marginBottom: '20px', color: '#96ca2d' }}></i>
							<p style={{ fontSize: '18px', fontWeight: '500' }}>{t('news.noResults')}</p>
						</div>
					) : (
						filteredPosts.slice((currentPage-1)*itemsPerPage, (currentPage)*itemsPerPage).map(post => (
							<div key={post.id} className="jet-posts__item" style={{ flex: '1 1 300px', maxWidth: '400px' }}>
								<div
									className="jet-posts__inner-box"
									style={{
									display: 'flex',
										flexDirection: 'column',
									background: '#fff',
									border: '2px solid #e5e7eb',
									borderRadius: '12px',
									overflow: 'hidden',
									boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
										height: '520px',
									transform: 'translateY(0)',
									transition: 'all 0.3s ease',
									cursor: 'default',
									position: 'relative'
								}}
								onMouseEnter={(e) => {
									e.currentTarget.style.borderColor = '#96ca2d';
									e.currentTarget.style.transform = 'translateY(-4px)';
									e.currentTarget.style.boxShadow = '0 8px 20px rgba(150, 202, 45, 0.2)';
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.borderColor = '#e5e7eb';
									e.currentTarget.style.transform = 'translateY(0)';
									e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
								}}
								>
									{/* Barre verte en haut */}
									<div style={{
										position: 'absolute',
										top: '0px',
										left: '0px',
										right: '0px',
										height: '3px',
										background: 'linear-gradient(135deg, rgb(150, 202, 45) 0%, rgb(127, 184, 0) 100%)',
										transform: 'translateY(0px)',
										borderColor: 'rgba(150, 202, 45, 0.1)',
										zIndex: 2
									}} />
									<div className="post-thumbnail" style={{ height: '180px', overflow: 'hidden', position: 'relative', flex: '0 0 180px' }}>
										<img
											src={post.img}
											alt={post.title}
												style={{
													width: '100%',
												height: '100%',
												objectFit: 'cover',
												transition: 'transform 0.3s ease'
											}}
											onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
											onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
										/>
										
										{/* Catégories sur l'image */}
										<div style={{ 
													position: 'absolute',
											top: '12px', 
											left: '12px', 
											display: 'flex', 
													flexWrap: 'wrap',
											gap: '6px' 
										}}>
											{post.originalCategories && post.originalCategories.map((category, index) => {
												// Utiliser le nom original normalisé en minuscules pour le filtrage
												const categoryIdForFilter = (category || '').toLowerCase().trim();
												return (
													<span
														key={index}
														onClick={(e) => {
															e.preventDefault();
															e.stopPropagation();
															scrollToTop();
															toggleCategory(categoryIdForFilter);
														}}
														style={{
															display: 'inline-block',
															padding: '4px 8px',
															background: 'rgba(150, 202, 45, 0.9)',
															color: '#ffffff',
															fontSize: '0.7rem',
															fontWeight: '600',
															borderRadius: '10px',
															textTransform: 'uppercase',
															letterSpacing: '0.5px',
															backdropFilter: 'blur(4px)',
															boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
															cursor: 'pointer',
															transition: 'all 0.3s ease'
														}}
														onMouseEnter={(e) => {
															e.target.style.background = 'rgba(150, 202, 45, 1)';
															e.target.style.transform = 'scale(1.05)';
															e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
														}}
														onMouseLeave={(e) => {
															e.target.style.background = 'rgba(150, 202, 45, 0.9)';
															e.target.style.transform = 'scale(1)';
															e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
														}}
													>
														{category}
													</span>
												);
											})}
</div>
</div>
									<div className="jet-posts__inner-content" style={{ 
										padding: '25px', 
										display: 'flex', 
										flexDirection: 'column', 
										gap: '15px', 
										flex: 1, 
										height: '320px', 
										justifyContent: 'space-between' 
									}}>
										<div>
											<h4 className="entry-title" style={{ 
												margin: '0 0 10px 0', 
												display: '-webkit-box', 
												WebkitLineClamp: 2, 
												WebkitBoxOrient: 'vertical', 
												overflow: 'hidden', 
												textOverflow: 'ellipsis', 
												fontSize: '1.1rem', 
												fontWeight: 'bold',
												height: '3rem',
												minHeight: '3rem',
												maxHeight: '3rem',
												lineHeight: '1.5'
											}}>
												<span style={{ color: '#2c3e50' }} title={post.title}>
													{post.title}
												</span>
											</h4>
											<div className="post-meta" style={{ 
												color: '#7f8c8d', 
												fontSize: '0.9rem', 
												marginBottom: '15px',
												height: '1.5rem',
												minHeight: '1.5rem',
												maxHeight: '1.5rem',
												display: 'flex',
												alignItems: 'center'
											}}>
												<time dateTime={post.date} title={post.date} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
													<i className="fas fa-calendar-alt" style={{ fontSize: '0.85rem', color: '#7f8c8d' }}></i>
													<span>{formatDate(post.date)}</span>
												</time>
</div>
											
											<div className="entry-excerpt" title={post.excerpt} style={{ 
												color: '#34495e', 
												fontSize: '0.95rem', 
												lineHeight: 1.6, 
												display: '-webkit-box', 
												WebkitLineClamp: 4, 
												WebkitBoxOrient: 'vertical', 
												overflow: 'hidden', 
												textOverflow: 'ellipsis',
												flex: 1,
												minHeight: '6.4rem',
												maxHeight: '6.4rem'
											}}>
												{post.excerpt.length > 150 ? post.excerpt.substring(0, 150) + '...' : post.excerpt}
											</div>
										</div>
										<div className="jet-more-wrap" style={{ marginTop: '5px' }}>
                          <a
                            href="#"
												className="btn btn-primary elementor-button elementor-size-md jet-more"
                            onClick={(e) => { e.preventDefault(); scrollToTop(); navigate(`/news/${post.id}`); }}
												style={{ 
													display: 'inline-flex',
													alignItems: 'center',
													gap: '8px',
													padding: '10px 20px',
													background: '#96ca2d',
													color: 'white',
													textDecoration: 'none',
													borderRadius: '25px',
													fontWeight: '600',
													fontSize: '0.9rem',
													transition: 'all 0.3s ease',
													width: 'auto',
													textAlign: 'center'
												}}
												onMouseEnter={(e) => {
													e.currentTarget.style.background = '#fff';
													e.currentTarget.style.color = '#96ca2d';
													e.currentTarget.style.border = '1px solid #96ca2d';
													e.currentTarget.style.transform = 'translateX(5px)';
												}}
												onMouseLeave={(e) => {
													e.currentTarget.style.background = '#96ca2d';
													e.currentTarget.style.color = 'white';
													e.currentTarget.style.border = 'none';
													e.currentTarget.style.transform = 'translateX(0)';
												}}
											>
												<span className="btn__text">{t('news.readMore','Lire la suite')}</span>
												<i className="fas fa-arrow-right" style={{ transition: 'transform 0.3s ease' }}></i>

											</a>
										</div>
</div>
</div>
							</div>
						))
					)}
</div>
					<div className="pagination-controls" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0, marginTop: '30px', flexWrap: 'wrap' }}>
						<div style={{ display: 'inline-flex', gap: 0 }}>
							<button
								onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
								disabled={currentPage === 1}
								style={{
									padding: '8px 10px',
									minWidth: 36,
									borderRadius: '6px 0 0 6px',
									border: '1px solid #96ca2d',
									background: currentPage === 1 ? '#f2f2f2' : '#96ca2d',
									color: currentPage === 1 ? '#888' : '#fff',
									cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
									margin: 0,
									borderRightWidth: 0
								}}
							>&lt;</button>
							{(() => {
								const buttons = [];
								const totalPages = Math.ceil(filteredPosts.length / itemsPerPage);
								const renderBtn = (i, isFirst = false) => (
									<button
										key={i}
										onClick={() => setCurrentPage(i)}
										style={{
											padding: '8px 10px',
											minWidth: 36,
											borderRadius: 0,
											border: `1px solid ${currentPage === i ? '#96ca2d' : '#ddd'}`,
											background: currentPage === i ? '#96ca2d' : '#fff',
											color: currentPage === i ? '#fff' : '#333',
											cursor: 'pointer',
											margin: 0,
											borderLeftWidth: isFirst ? '1px' : 0
										}}
									>
										{i}
									</button>
								);

								const shown = Math.min(totalPages, 3);
								for (let i = 1; i <= shown; i++) {
									buttons.push(renderBtn(i, i === 1));
								}
								if (totalPages > 3) {
									buttons.push(
										<span key="ell" style={{ padding: '8px 6px', border: '1px solid #ddd', borderLeftWidth: 0 }}>...</span>
									);
								}
								return buttons;
							})()}
							<button
								onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredPosts.length / itemsPerPage), p + 1))}
								disabled={currentPage === Math.ceil(filteredPosts.length / itemsPerPage)}
								style={{
									padding: '8px 10px',
									minWidth: 36,
									borderRadius: '0 6px 6px 0',
									border: '1px solid #96ca2d',
									background: currentPage === Math.ceil(filteredPosts.length / itemsPerPage) ? '#f2f2f2' : '#96ca2d',
									color: currentPage === Math.ceil(filteredPosts.length / itemsPerPage) ? '#888' : '#fff',
									cursor: currentPage === Math.ceil(filteredPosts.length / itemsPerPage) ? 'not-allowed' : 'pointer',
									margin: 0,
									borderLeftWidth: 0
								}}
							>&gt;</button>
						</div>
</div>
				</main>

				<aside className="news-sidebar">
					<div style={{ marginBottom: 25 }}>
						{renderSearchForm()}
					</div>

					{/* Tri */}
					<div className="sort-box" style={{ marginBottom: 50 }}>
						<h4 className="results-title" style={{ fontSize: "1rem", marginBottom: 8, fontWeight: 'bold' }}>{t('news.sort.title', 'Trier par')}</h4>
						<div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
							<button 
								onClick={() => setSortBy(sortBy === 'newest' ? 'oldest' : 'newest')}
								className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-green-500`}
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
									padding: '8px 16px',
									borderRadius: '9999px',
									background: '#96ca2d',
									color: '#fff',
									border: 'none',
									fontWeight: '600',
									fontSize: '14px',
									cursor: 'pointer',
									transition: 'all 0.2s ease',
									outline: 'none'
								}}
								onMouseEnter={(e) => {
									e.target.style.background = '#96ca2d';
								}}
								onMouseLeave={(e) => {
									e.target.style.background = '#96ca2d';
								}}
								aria-label={sortBy === 'newest' ? t('news.sort.changeToOldest') : t('news.sort.changeToNewest')}
							>
								<svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" aria-hidden="true" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
									<path d="M304 416h-64a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h64a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zM16 160h48v304a16 16 0 0 0 16 16h32a16 16 0 0 0 16-16V160h48c14.21 0 21.38-17.24 11.31-27.31l-80-96a16 16 0 0 0-22.62 0l-80 96C-5.35 142.74 1.77 160 16 160zm416 0H240a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h192a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zm-64 128H240a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h128a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zM496 32H240a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h256a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16z"></path>
								</svg>
								<span>{sortBy === 'newest' ? t('news.sort.newestToOldest') : t('news.sort.oldestToNewest')}</span>
							</button>
						</div>
					</div>

					{/* Articles récents */}
					<div  style={{ marginBottom:50 }}>
						<h4 className="results-title" style={{ fontSize: "1rem", marginBottom: 8, fontWeight: 'bold' }}>{t('news.recent', 'Articles récents')}</h4>
						<ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
							{posts.slice(0, 5).map(p => (
								<li key={p.id} style={{ display: 'grid', gridTemplateColumns: '64px 1fr', gap: 8, alignItems: 'center' }}>
									<img src={p.img} alt={p.title} style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 6 }} />
									<div>
										<a 
											href="#" 
											onClick={(e) => { e.preventDefault(); navigate(`/news/${p.id}`); }}
											style={{ 
												fontSize: '1rem',
												color: '#212529', 
												textDecoration: 'none',
												transition: 'color 0.2s ease, text-decoration 0.2s ease'
											}}
											onMouseEnter={(e) => {
												e.target.style.color = '#96ca2d';
												e.target.style.textDecoration = 'underline';
											}}
											onMouseLeave={(e) => {
												e.target.style.color = '#212529';
												e.target.style.textDecoration = 'none';
											}}
											onFocus={(e) => {
												e.target.style.color = '#96ca2d';
												e.target.style.textDecoration = 'underline';
											}}
											onBlur={(e) => {
												e.target.style.color = '#212529';
												e.target.style.textDecoration = 'none';
											}}
										>
											{p.title}
										</a>
										<div style={{ fontSize: 12, color: '#6c757d', display: 'flex', alignItems: 'center', gap: '6px' }}>
											<i className="fas fa-calendar-alt" style={{ fontSize: '0.7rem', color: '#6c757d' }}></i>
											<span>{formatDate(p.date)}</span>
										</div>
									</div>
								</li>
							))}
						</ul>
</div>

					{/* Archives */}
					<div  style={{ marginBottom: 50 }}>
						<h4 className="results-title" style={{ fontSize: "1rem", marginBottom: 8, fontWeight: 'bold' }}>{t('news.archives.title', 'Archives')}</h4>
						<ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 4 }}>
							{archives.map(a => (
								<li key={a.key}>
									<span 
										style={{ 
											display: 'block', 
											color: (selectedArchive===a.key || (a.key==='all' && !selectedArchive)) ? '#96ca2d' : '#6c757d', 
											cursor: 'pointer', 
											paddingLeft: '12px', 
											fontSize: '15px',
											textDecoration: 'none',
											transition: 'color 0.2s ease, text-decoration 0.2s ease'
										}} 
										onClick={() => toggleArchive(a.key)}
										onMouseEnter={(e) => {
											e.target.style.color = '#96ca2d';
											e.target.style.textDecoration = 'underline';
										}}
										onMouseLeave={(e) => {
											e.target.style.color = (selectedArchive===a.key || (a.key==='all' && !selectedArchive)) ? '#96ca2d' : '#6c757d';
											e.target.style.textDecoration = 'none';
										}}
										onFocus={(e) => {
											e.target.style.color = '#96ca2d';
											e.target.style.textDecoration = 'underline';
										}}
										onBlur={(e) => {
											e.target.style.color = (selectedArchive===a.key || (a.key==='all' && !selectedArchive)) ? '#96ca2d' : '#6c757d';
											e.target.style.textDecoration = 'none';
										}}
									>
										{a.label}
									</span>
								</li>
							))}
						</ul>
              </div>

					{/* Catégories */}
					<div className="widget" style={{ marginBottom: 12 }}>
						<h4 className="results-title" style={{ fontSize: "1rem", marginBottom: 8, fontWeight: 'bold' }}>{t('news.categories.title', 'Catégories')}</h4>
						<div className="category-filters" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', marginBottom: 8, gap: 4 }}>
							{categories.map(cat => {
								const isChecked = cat.id === 'all' ? selectedCategories.length === 0 : selectedCategories.includes(cat.id);
								return (
									<label
										key={cat.id}
										onClick={(e) => {
											e.preventDefault();
											toggleCategory(cat.id);
										}}
								style={{ 
									width: '100%', 
											display: 'flex',
											alignItems: 'center',
											gap: '8px',
									cursor: 'pointer', 
									paddingLeft: '12px', 
									fontSize: '15px',
											color: isChecked ? '#96ca2d' : '#6c757d',
											transition: 'color 0.2s ease'
								}}
								onMouseEnter={(e) => {
											e.currentTarget.style.color = '#96ca2d';
								}}
								onMouseLeave={(e) => {
											e.currentTarget.style.color = isChecked ? '#96ca2d' : '#6c757d';
										}}
									>
										<input
											type="checkbox"
											checked={isChecked}
											onChange={() => toggleCategory(cat.id)}
											onClick={(e) => e.stopPropagation()}
									style={{ 
												width: '18px',
												height: '18px',
										cursor: 'pointer', 
												accentColor: '#96ca2d',
												flexShrink: 0
											}}
										/>
										<span>{cat.name}</span>
									</label>
								);
							})}
						</div>
</div>
				</aside>
</div>
</section>

		{/* Carte Géographique Interactive MedAgriCycle */}

		<Footer/>
		</div></div></div>
    </>
  );
};

export default News;